import type { Client } from "../../../domain/client/client.js";
import { ClientStatus } from "../../../domain/client/client.js";
import type { User } from "../../../domain/identity/user.js";
import { UserStatus } from "../../../domain/identity/user.js";
import type { Professional } from "../../../domain/identity/professional.js";
import { ProfessionalStatus } from "../../../domain/identity/professional.js";
import type { Role, UserClientAccess } from "../../../domain/identity/access-control.js";
import { UserClientAccessStatus } from "../../../domain/identity/access-control.js";
import { RoleCode } from "../../../domain/identity/access-types.js";
import type { ClientId, UserId, ProfessionalId, RoleId, UserClientAccessId } from "../../../shared/kernel/identifiers.js";

/**
 * Extraída para permitir um repositório SQLite real (`SqliteIdentityRepository`,
 * correção multiusuário) no lugar desta classe em memória, sem alterar quem a
 * consome (`AdminService`, `RdoService`): eles passam a depender da forma, não
 * da classe concreta.
 */
export interface IdentityRepository {
  listClients(): Promise<Client[]>;
  getClient(id: ClientId): Promise<Client | null>;
  saveClient(client: Client): Promise<void>;
  inactivateClient(id: ClientId): Promise<void>;
  deleteClient(id: ClientId): Promise<void>;

  listUsers(): Promise<User[]>;
  getUser(id: UserId): Promise<User | null>;
  saveUser(user: User): Promise<void>;
  inactivateUser(id: UserId): Promise<void>;
  deleteUser(id: UserId): Promise<void>;

  listProfessionals(clientId?: ClientId): Promise<Professional[]>;
  getProfessional(id: ProfessionalId): Promise<Professional | null>;
  saveProfessional(professional: Professional): Promise<void>;
  inactivateProfessional(id: ProfessionalId): Promise<void>;
  deleteProfessional(id: ProfessionalId): Promise<void>;

  saveUserProfileImage(userId: string, dataUrl: string): Promise<string>;
  getUserProfileImage(userId: string): Promise<string | null>;
  deleteUserProfileImage(userId: string): Promise<void>;

  saveProfessionalSignature(professionalId: string, base64: string): Promise<string>;
  getProfessionalSignature(professionalId: string): Promise<string | null>;
  deleteProfessionalSignature(professionalId: string): Promise<void>;

  listAccesses(userId?: UserId, clientId?: ClientId): Promise<UserClientAccess[]>;
  saveAccess(access: UserClientAccess): Promise<void>;
  revokeAccess(id: UserClientAccessId): Promise<void>;

  listRoles(clientId?: ClientId): Promise<Role[]>;
  saveRole(role: Role): Promise<void>;
}

export class InMemoryIdentityRepository implements IdentityRepository {
  private clients = new Map<string, Client>();
  // Vazio de propósito: o admin padrão (com login/senha) é instalado por
  // initDefaultsIfEmpty() logo abaixo. Um valor inicial aqui (mesmo que só
  // com id/email/nome) faria this.users.size nunca ser 0 na primeira
  // inicialização, e o admin completo (login/username/password/passwordHash)
  // nunca seria criado — login por "admin" jamais encontraria esse usuário
  // por login/username, só por e-mail. Bug real corrigido nesta revisão.
  private users = new Map<string, User>();
  private professionals = new Map<string, Professional>();
  private professionalSignatures = new Map<string, string>();
  private userProfileImages = new Map<string, string>();
  private accesses = new Map<string, UserClientAccess>();
  private roles = new Map<string, Role>();

  constructor() {
    this.load();
    this.initDefaultsIfEmpty();
  }

  private load(): void {
    try {
      const c = localStorage.getItem("inspect_clients");
      if (c) this.clients = new Map(JSON.parse(c));
      const u = localStorage.getItem("inspect_users");
      if (u) {
        const loadedUsers: [string, User][] = JSON.parse(u);
        const mappedUsers = loadedUsers.map(([id, user]) => {
          const userLogin = user.login || user.username || user.email.split('@')[0] || id;
          // Registro legado do admin de bootstrap: versões anteriores
          // persistiam "usr_admin" sem profileType (o seed completo nunca
          // rodava, ver initDefaultsIfEmpty). Sem o Role ele entra como
          // VIEWER e fica sem acesso à própria tela de Usuários para se
          // corrigir. Só o id de bootstrap é completado — nunca se atribui
          // Role a outro usuário aqui.
          const legacyBootstrapAdmin = id === "usr_admin" && !user.profileType;
          return [id, {
            ...user,
            login: userLogin,
            username: userLogin,
            // Senha em texto NÃO é reintroduzida ao reler o cadastro: o
            // campo `password` era regravado a cada carga, desfazendo
            // qualquer migração para o formato seguro.
            password: undefined,
            passwordHash: user.passwordHash || (user.password ? `hash_${user.password}` : 'hash_123456'),
            ...(legacyBootstrapAdmin ? { profileType: RoleCode.Admin } : {})
          }] as [string, User];
        });
        this.users = new Map(mappedUsers);
      }
      const p = localStorage.getItem("inspect_professionals");
      if (p) this.professionals = new Map(JSON.parse(p));
      const pSigs = localStorage.getItem("inspect_professional_signatures");
      if (pSigs) this.professionalSignatures = new Map(JSON.parse(pSigs));
      const uPhotos = localStorage.getItem("inspect_user_profile_images");
      if (uPhotos) this.userProfileImages = new Map(JSON.parse(uPhotos));
      const a = localStorage.getItem("inspect_accesses");
      if (a) this.accesses = new Map(JSON.parse(a));
      const r = localStorage.getItem("inspect_roles");
      if (r) this.roles = new Map(JSON.parse(r));
    } catch (e) {
      console.warn("Failed to load identity from localStorage", e);
    }
  }

  private persist(): void {
    try {
      localStorage.setItem("inspect_clients", JSON.stringify(Array.from(this.clients.entries())));
      localStorage.setItem("inspect_users", JSON.stringify(Array.from(this.users.entries())));
      localStorage.setItem("inspect_professionals", JSON.stringify(Array.from(this.professionals.entries())));
      localStorage.setItem("inspect_professional_signatures", JSON.stringify(Array.from(this.professionalSignatures.entries())));
      localStorage.setItem("inspect_user_profile_images", JSON.stringify(Array.from(this.userProfileImages.entries())));
      localStorage.setItem("inspect_accesses", JSON.stringify(Array.from(this.accesses.entries())));
      localStorage.setItem("inspect_roles", JSON.stringify(Array.from(this.roles.entries())));
    } catch (e) {
      console.warn("Failed to persist identity to localStorage", e);
    }
  }

  private initDefaultsIfEmpty(): void {
    // Clients iniciam vazios - sem clientes fictícios ou de demonstração.
    // O sistema aguarda o cadastro do primeiro cliente real pelo Administrador.

    if (this.users.size === 0) {
      const usersList: User[] = [
        {
          id: "usr_admin" as UserId,
          name: "Administrador do Sistema",
          email: "admin@inspect.ai",
          login: "admin",
          username: "admin",
          // Credencial de bootstrap em formato antigo, de propósito: a
          // derivação PBKDF2 é assíncrona e este caminho é síncrono. Não é
          // segredo (é a senha padrão documentada) e o PRIMEIRO login já a
          // regrava em formato seguro — ver AdminService.authenticateByCredentials.
          passwordHash: "hash_123456",
          // Sem profileType o Role de autorização (User.profileType, ver
          // CLAUDE.md secão 8) ficava indefinido e o admin padrão entrava
          // com o fallback mínimo (VIEWER) em vez de administrador — bug
          // real corrigido nesta revisão (achado ao testar o login de
          // homologação ponta a ponta).
          profileType: RoleCode.Admin,
          status: UserStatus.Active,
          authenticationId: "auth_admin",
          lastLoginAt: new Date().toISOString() as any,
          lastAccessAt: new Date().toISOString() as any,
          version: 1,
          createdAt: new Date().toISOString() as any,
          updatedAt: new Date().toISOString() as any,
          deletedAt: null,
          deletedBy: null,
          deletionReason: null
        }
      ];

      for (const u of usersList) {
        this.users.set(u.id, u);
      }
    }
    if (this.accesses.size === 0 && this.clients.has("inspect-main" as ClientId)) {
      const access1: UserClientAccess = {
        id: "uca-admin-main" as UserClientAccessId,
        userId: "usr_admin" as UserId,
        clientId: "inspect-main" as ClientId,
        roleIds: ["role_admin" as RoleId],
        status: UserClientAccessStatus.Active,
        version: 1,
        createdAt: new Date().toISOString() as any,
        updatedAt: new Date().toISOString() as any,
        deletedAt: null,
        deletedBy: null,
        deletionReason: null
      };
      this.accesses.set(access1.id, access1);
    }

    this.persist();
  }

  // Clients
  async listClients(): Promise<Client[]> {
    return Array.from(this.clients.values());
  }

  async getClient(id: ClientId): Promise<Client | null> {
    return this.clients.get(id) || null;
  }

  async saveClient(client: Client): Promise<void> {
    this.clients.set(client.id, client);
    this.persist();
  }

  async inactivateClient(id: ClientId): Promise<void> {
    const c = this.clients.get(id);
    if (c) {
      this.clients.set(id, { ...c, status: ClientStatus.Inactive, updatedAt: new Date().toISOString() as any });
      this.persist();
    }
  }

  async deleteClient(id: ClientId): Promise<void> {
    if (this.clients.has(id)) {
      this.clients.delete(id);
      this.persist();
    }
  }

  /**
   * O vínculo User ↔ Professional é gravado só num sentido pelo cadastro
   * oficial (Professional.userId, ver AdminService.createProfessional), mas
   * os gates de PLH (canActAsPlh, requireAssignedPlh, approveByPlh…) leem
   * User.professionalId. Sem esta leitura pelos dois sentidos, todo PLH criado
   * pela tela ficava "sem Profissional vinculado". Só completa quando falta;
   * nunca sobrescreve um vínculo já gravado.
   */
  private withLinkedProfessional(user: User): User {
    if (user.professionalId) return user;
    for (const p of this.professionals.values()) {
      if (String(p.userId || "") === String(user.id) && p.status === ProfessionalStatus.Active && !p.deletedAt) {
        return { ...user, professionalId: String(p.id) };
      }
    }
    return user;
  }

  // Users
  async listUsers(): Promise<User[]> {
    return Array.from(this.users.values()).map(u => this.withLinkedProfessional(u));
  }

  async getUser(id: UserId): Promise<User | null> {
    const user = this.users.get(id);
    return user ? this.withLinkedProfessional(user) : null;
  }

  async saveUser(user: User): Promise<void> {
    this.users.set(user.id, user);
    this.persist();
  }

  async inactivateUser(id: UserId): Promise<void> {
    const u = this.users.get(id);
    if (u) {
      this.users.set(id, { ...u, status: UserStatus.Inactive, updatedAt: new Date().toISOString() as any });
      this.persist();
    }
  }

  async deleteUser(id: UserId): Promise<void> {
    if (this.users.has(id)) {
      this.users.delete(id);
      this.persist();
    }
  }

  // Professionals
  async listProfessionals(clientId?: ClientId): Promise<Professional[]> {
    const all = Array.from(this.professionals.values());
    if (clientId) {
      return all.filter(p => p.clientId === clientId);
    }
    return all;
  }

  async getProfessional(id: ProfessionalId): Promise<Professional | null> {
    return this.professionals.get(id) || null;
  }

  async saveProfessional(professional: Professional): Promise<void> {
    this.professionals.set(professional.id, professional);
    this.persist();
  }

  async inactivateProfessional(id: ProfessionalId): Promise<void> {
    const p = this.professionals.get(id);
    if (p) {
      this.professionals.set(id, { ...p, status: ProfessionalStatus.Inactive, updatedAt: new Date().toISOString() as any });
      this.persist();
    }
  }

  async deleteProfessional(id: ProfessionalId): Promise<void> {
    if (this.professionals.has(id)) {
      this.professionals.delete(id);
      this.persist();
    }
  }

  // --- User profile photos (Bloco 21) ---
  // Stored apart from professional signatures, keyed by userId, so a photo is
  // never mistaken for a signature and vice versa.

  async saveUserProfileImage(userId: string, dataUrl: string): Promise<string> {
    const imageId = `img_${userId}`;
    this.userProfileImages.set(imageId, dataUrl);
    this.persist();
    return imageId;
  }

  async getUserProfileImage(userId: string): Promise<string | null> {
    return this.userProfileImages.get(`img_${userId}`) || null;
  }

  async deleteUserProfileImage(userId: string): Promise<void> {
    const imageId = `img_${userId}`;
    if (this.userProfileImages.has(imageId)) {
      this.userProfileImages.delete(imageId);
      this.persist();
    }
  }

  async saveProfessionalSignature(professionalId: string, base64: string): Promise<string> {
    const signatureId = `sig_${professionalId}`;
    this.professionalSignatures.set(signatureId, base64);
    this.persist();
    return signatureId;
  }

  async getProfessionalSignature(professionalId: string): Promise<string | null> {
    const signatureId = `sig_${professionalId}`;
    return this.professionalSignatures.get(signatureId) || null;
  }

  async deleteProfessionalSignature(professionalId: string): Promise<void> {
    const signatureId = `sig_${professionalId}`;
    if (this.professionalSignatures.has(signatureId)) {
      this.professionalSignatures.delete(signatureId);
      this.persist();
    }
  }

  // User Client Access
  async listAccesses(userId?: UserId, clientId?: ClientId): Promise<UserClientAccess[]> {
    return Array.from(this.accesses.values()).filter(a => {
      if (userId && a.userId !== userId) return false;
      if (clientId && a.clientId !== clientId) return false;
      return true;
    });
  }

  async saveAccess(access: UserClientAccess): Promise<void> {
    this.accesses.set(access.id, access);
    this.persist();
  }

  async revokeAccess(id: UserClientAccessId): Promise<void> {
    const a = this.accesses.get(id);
    if (a) {
      this.accesses.set(id, { ...a, status: UserClientAccessStatus.Revoked, updatedAt: new Date().toISOString() as any });
      this.persist();
    }
  }

  // Roles
  async listRoles(clientId?: ClientId): Promise<Role[]> {
    const all = Array.from(this.roles.values());
    if (clientId) {
      return all.filter(r => r.clientId === clientId || r.clientId === null);
    }
    return all;
  }

  async saveRole(role: Role): Promise<void> {
    this.roles.set(role.id, role);
    this.persist();
  }
}
